import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import PageShell from './PageShell';
import {
  ArrowLeft, ExternalLink, TrendingUp, TrendingDown, Minus, AlertTriangle,
  ShieldAlert, Users, Bike, Car, Footprints, Truck, MapPin, FileBarChart,
  RefreshCw, BookOpen, GraduationCap, Target, MessageSquare, ListChecks, Lightbulb,
  Presentation, X, ChevronLeft, ChevronRight, Maximize2, Clock, Printer, QrCode,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCE_URL_FALLBACK = 'https://www.onisr.securite-routiere.gouv.fr/sites/default/files/2026-06/Barometre_M_mai_2026%20ONISR.pdf';

// Fallback statique (utilisé si l'API échoue) — MAI 2026
const FALLBACK = {
  label: 'mai 2026',
  source_url: SOURCE_URL_FALLBACK,
  stats: {
    headline: { accidents: 4564, tues: 303, tues_n_minus_1: 284, tues_delta: 19, blesses_graves: 1585 },
    cumul: { accidents: 20280, tues: 1262, blesses_graves: 6624 },
    rolling: { tues_12m: 3361, blesses_graves_12m: 17113 },
    evolutions: {
      usagers_tues: [
        { label: 'cyclistes', delta: '+12' },
        { label: 'piétons', delta: '+7' },
        { label: 'occupants de véhicule de tourisme', delta: '+5' },
        { label: 'deux-roues motorisé', delta: '-9' },
      ],
      ages: [
        { label: '25-64 ans', delta: '+16' },
        { label: '18-24 ans', delta: '+6' },
        { label: 'moins de 18 ans', delta: '+2' },
        { label: '65 ans ou plus', delta: '-7' },
      ],
      reseaux: [
        { label: 'autoroutes', delta: '+17' },
        { label: 'hors agglomération', delta: '+2' },
        { label: 'en agglomération', delta: '0' },
      ],
    },
    narrative: '',
  },
};

const ICON_USAGER = {
  cyclistes: Bike,
  'piétons': Footprints,
  'occupants de véhicule de tourisme': Car,
  'deux-roues motorisé': Truck,
};

function fmt(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}

function deltaSign(d) {
  if (!d || d === '0') return 'flat';
  if (d.startsWith('+')) return 'up';
  if (d.startsWith('-')) return 'down';
  return 'flat';
}

function EvoBadge({ sign, children, className = '' }) {
  const map = {
    up:   { Icon: TrendingUp,   cls: 'bg-red-600/20 border-red-700/40 text-red-300' },
    down: { Icon: TrendingDown, cls: 'bg-emerald-600/20 border-emerald-700/40 text-emerald-300' },
    flat: { Icon: Minus,        cls: 'bg-white/5 border-white/15 text-white/70' },
  };
  const { Icon, cls } = map[sign] || map.flat;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11.5px] font-bold font-mono-spec ${cls} ${className}`}>
      <Icon size={11} /> {children}
    </span>
  );
}

function StatCard({ label, value, evo, evoSign, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">{label}</div>
      <div className="font-serif-editorial text-white text-[42px] lg:text-[48px] leading-none mt-2">{value}</div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {evo && <EvoBadge sign={evoSign}>{evo}</EvoBadge>}
        {sub && <span className="text-white/55 text-[12.5px]">{sub}</span>}
      </div>
    </div>
  );
}

// ============================================================
// PRESENTATION MODE — slides for classroom projection
// ============================================================

const SLIDES = [
  { kicker: 'Baromètre ONISR · Source officielle', title: 'Mai 2026', sub: 'Sécurité routière — France métropolitaine', mode: 'cover' },
  { kicker: 'Mai 2026', title: '303 tués', sub: '+19 par rapport à mai 2025 · +7 %', mode: 'big' },
  { kicker: 'Mai 2026', title: '1 585', sub: 'Blessés graves (M.AIS3+) · stable vs N-1', mode: 'big' },
  { kicker: 'Cumul 2026 · janvier à mai', title: '1 262 tués', sub: '+8 % · 6 624 blessés graves (+6 %)', mode: 'big' },
  { kicker: 'Évolution par usager · vs mai 2025', title: 'Les vulnérables paient', sub: '', mode: 'usagers' },
  { kicker: 'Évolution par âge · vs mai 2025', title: 'Les actifs en première ligne', sub: '', mode: 'ages' },
  { kicker: 'Évolution par réseau · vs mai 2025', title: 'Autoroute : la zone rouge', sub: '+17 tués sur autoroute en un mois', mode: 'reseaux' },
  { kicker: '3 questions pour ta classe', title: 'À débattre maintenant', sub: '', mode: 'questions' },
  { kicker: 'Engagement personnel', title: '« Cette semaine, je vais… »', sub: 'Chaque élève écrit son engagement de la semaine', mode: 'pledge' },
  { kicker: 'Continue sur ton smartphone', title: 'Scanne ce QR code', sub: 'Mises à jour automatiques chaque mois', mode: 'qr' },
  { kicker: 'Sources officielles', title: 'ONISR + Sécurité Routière', sub: 'onisr.securite-routiere.gouv.fr', mode: 'sources' },
];

function PresentationSlide({ slide, stats, pageUrl }) {
  const u = stats.evolutions?.usagers_tues || [];
  const a = stats.evolutions?.ages || [];
  const r = stats.evolutions?.reseaux || [];
  return (
    <div className="flex flex-col items-center justify-center text-center max-w-5xl mx-auto px-8">
      <div className="text-[14px] uppercase tracking-[0.32em] text-red-400 font-bold font-mono-spec mb-8">{slide.kicker}</div>
      {slide.mode === 'cover' && (
        <>
          <h1 className="font-serif-editorial text-white text-[120px] lg:text-[180px] leading-[0.95] mb-6">{slide.title}</h1>
          <p className="text-white/70 text-[28px]">{slide.sub}</p>
        </>
      )}
      {slide.mode === 'big' && (
        <>
          <h1 className="font-serif-editorial text-red-500 text-[150px] lg:text-[200px] leading-[0.9] mb-6">{slide.title}</h1>
          <p className="text-white/85 text-[30px] max-w-3xl">{slide.sub}</p>
        </>
      )}
      {slide.mode === 'usagers' && (
        <>
          <h1 className="font-serif-editorial text-white text-[60px] lg:text-[80px] leading-tight mb-10">{slide.title}</h1>
          <div className="grid grid-cols-2 gap-6 w-full max-w-4xl">
            {u.map((x) => (
              <div key={x.label} className={`rounded-2xl border-2 p-6 text-left ${deltaSign(x.delta)==='up'?'border-red-600 bg-red-950/30':'border-emerald-600 bg-emerald-950/20'}`}>
                <div className="text-white text-[24px] font-bold capitalize">{x.label}</div>
                <div className={`font-serif-editorial text-[56px] mt-2 ${deltaSign(x.delta)==='up'?'text-red-400':'text-emerald-400'}`}>{x.delta} tués</div>
              </div>
            ))}
          </div>
        </>
      )}
      {slide.mode === 'ages' && (
        <>
          <h1 className="font-serif-editorial text-white text-[60px] lg:text-[80px] leading-tight mb-10">{slide.title}</h1>
          <div className="grid grid-cols-2 gap-5 w-full max-w-3xl">
            {a.map((x) => (
              <div key={x.label} className={`rounded-2xl border-2 p-6 text-left ${deltaSign(x.delta)==='up'?'border-red-600 bg-red-950/30':'border-emerald-600 bg-emerald-950/20'}`}>
                <div className="text-white text-[22px] font-bold">{x.label}</div>
                <div className={`font-serif-editorial text-[52px] mt-2 ${deltaSign(x.delta)==='up'?'text-red-400':'text-emerald-400'}`}>{x.delta} tués</div>
              </div>
            ))}
          </div>
        </>
      )}
      {slide.mode === 'reseaux' && (
        <>
          <h1 className="font-serif-editorial text-white text-[60px] lg:text-[80px] leading-tight mb-6">{slide.title}</h1>
          <p className="text-red-300 text-[28px] mb-10">{slide.sub}</p>
          <div className="grid grid-cols-3 gap-5 w-full max-w-4xl">
            {r.map((x) => (
              <div key={x.label} className={`rounded-2xl border-2 p-6 ${deltaSign(x.delta)==='up'?'border-red-600 bg-red-950/30':'border-white/15 bg-white/5'}`}>
                <div className="text-white text-[20px] font-bold capitalize">{x.label}</div>
                <div className={`font-serif-editorial text-[44px] mt-2 ${deltaSign(x.delta)==='up'?'text-red-400':'text-white/80'}`}>
                  {x.delta === '0' ? 'stable' : `${x.delta} tués`}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {slide.mode === 'questions' && (
        <>
          <h1 className="font-serif-editorial text-white text-[60px] lg:text-[80px] leading-tight mb-10">{slide.title}</h1>
          <div className="space-y-6 text-left w-full max-w-4xl text-white/90 text-[28px] lg:text-[32px] font-serif-editorial leading-tight">
            <div className="flex gap-5"><span className="text-red-500 font-bold">1.</span><span>Pourquoi <span className="text-red-400">+12 cyclistes tués</span> en mai ?</span></div>
            <div className="flex gap-5"><span className="text-red-500 font-bold">2.</span><span>Pourquoi <span className="text-red-400">+17 tués sur autoroute</span> ce mois-ci ?</span></div>
            <div className="flex gap-5"><span className="text-red-500 font-bold">3.</span><span>Quel comportement vais-je changer <span className="text-red-400">cette semaine</span> ?</span></div>
          </div>
        </>
      )}
      {slide.mode === 'pledge' && (
        <>
          <h1 className="font-serif-editorial text-red-500 text-[80px] lg:text-[110px] leading-[0.95] mb-8">{slide.title}</h1>
          <p className="text-white/85 text-[30px] max-w-3xl">{slide.sub}</p>
          <div className="mt-12 px-10 py-6 rounded-2xl border-2 border-white/20 bg-white/5 text-white/60 text-[26px] font-mono-spec">
            ___________________________________ parce que ___________________________________
          </div>
        </>
      )}
      {slide.mode === 'qr' && (
        <>
          <h1 className="font-serif-editorial text-white text-[60px] lg:text-[80px] leading-tight mb-3">{slide.title}</h1>
          <p className="text-white/75 text-[26px] mb-8">{slide.sub}</p>
          <div className="bg-white p-6 rounded-3xl shadow-2xl">
            <QRCodeSVG value={pageUrl} size={340} level="M" includeMargin={false} />
          </div>
          <p className="mt-6 text-red-400 text-[22px] font-mono-spec font-bold tracking-wider">{pageUrl.replace(/^https?:\/\//, '')}</p>
          <p className="mt-3 text-white/45 text-[15px]">Ouvre l&apos;appareil photo de ton téléphone et vise le code</p>
        </>
      )}
      {slide.mode === 'sources' && (
        <>
          <h1 className="font-serif-editorial text-white text-[60px] lg:text-[80px] leading-tight mb-6">{slide.title}</h1>
          <p className="text-white/70 text-[28px] mb-10">{slide.sub}</p>
          <div className="text-white/55 text-[20px] font-mono-spec space-y-2 max-w-3xl">
            <div>• onisr.securite-routiere.gouv.fr</div>
            <div>• securite-routiere.gouv.fr</div>
            <div>• Données arrêtées au 08/06/2026</div>
            <div className="mt-8 text-red-400 text-[18px]">codevoiture.fr · décryptage pédagogique</div>
          </div>
        </>
      )}
    </div>
  );
}

function PresentationMode({ slideIdx, setSlideIdx, onClose, stats, label, pageUrl }) {
  const slide = SLIDES[slideIdx];
  return (
    <div className="fixed inset-0 z-[200] bg-[#0a0a0b] flex flex-col" data-testid="onisr-presentation-mode">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10.5px] uppercase tracking-[0.32em] text-red-400 font-bold font-mono-spec">Mode présentation · ONISR {label}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/55 text-[12.5px] font-mono-spec">{slideIdx + 1} / {SLIDES.length}</span>
          <button onClick={onClose} data-testid="presentation-close" className="w-9 h-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 text-white flex items-center justify-center transition" aria-label="Quitter">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Slide */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <PresentationSlide slide={slide} stats={stats} pageUrl={pageUrl} />
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-white/5">
        <button onClick={() => setSlideIdx(Math.max(0, slideIdx - 1))} disabled={slideIdx === 0} data-testid="presentation-prev" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-white/5 text-white text-[13px] font-semibold transition">
          <ChevronLeft size={14} /> Précédent
        </button>
        <div className="flex-1 flex items-center gap-1.5 justify-center flex-wrap">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlideIdx(i)}
              data-testid={`presentation-dot-${i}`}
              className={`w-2 h-2 rounded-full transition ${i === slideIdx ? 'bg-red-500 w-6' : 'bg-white/20 hover:bg-white/40'}`}
              aria-label={`Aller à la diapositive ${i + 1}`}
            />
          ))}
        </div>
        <button onClick={() => setSlideIdx(Math.min(SLIDES.length - 1, slideIdx + 1))} disabled={slideIdx === SLIDES.length - 1} data-testid="presentation-next" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-30 disabled:hover:bg-red-600 text-white text-[13px] font-semibold shadow-lg shadow-red-900/40 transition">
          Suivant <ChevronRight size={14} />
        </button>
      </div>
      <div className="px-6 pb-3 text-center text-white/30 text-[11px] font-mono-spec">
        ← → Naviguer · Espace = suivant · Échap = quitter
      </div>
    </div>
  );
}

export default function OnisrMai2026Page() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);

  const loadData = (force = false) => {
    if (force) setRefreshing(true);
    axios.get(`${API}/onisr/latest${force ? '?force=1' : ''}`)
      .then((r) => { setData(r.data); setLoading(false); setRefreshing(false); })
      .catch((e) => { setError(e.message); setData(FALLBACK); setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { loadData(false); }, []);

  // Keyboard nav for presentation mode
  useEffect(() => {
    if (!presenting) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setPresenting(false);
      else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        setSlideIdx((i) => Math.min(i + 1, SLIDES.length - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        setSlideIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Home') setSlideIdx(0);
      else if (e.key === 'End') setSlideIdx(SLIDES.length - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [presenting]);

  const d = data || FALLBACK;
  const s = d.stats || FALLBACK.stats;
  const fetchedAt = d.fetched_at ? new Date(d.fetched_at) : null;
  const stale = d.stale === true;
  const sourceUrl = d.source_url || SOURCE_URL_FALLBACK;
  const label = d.label || FALLBACK.label;
  const pageUrl = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : 'https://codevoiture.fr/onisr-mai-2026';

  const headline = [
    { label: `Tués (${label})`, value: fmt(s.headline?.tues), evo: s.headline?.tues_delta != null ? `+${s.headline.tues_delta} vs N-1` : null, evoSign: 'up', sub: s.headline?.tues_n_minus_1 ? `${fmt(s.headline.tues_n_minus_1)} il y a un an` : '' },
    { label: 'Blessés graves', value: fmt(s.headline?.blesses_graves), evo: 'estimation ONISR', evoSign: 'flat', sub: 'définition M.AIS3+' },
    { label: 'Accidents corporels', value: fmt(s.headline?.accidents), evo: null, evoSign: 'flat', sub: 'enregistrés par les forces de l\u2019ordre' },
  ];

  const cumul = [
    { label: `Tués · cumul ${d.data_year || '2026'}`, value: fmt(s.cumul?.tues), evoSign: 'up', sub: 'depuis le 1ᵉʳ janvier' },
    { label: 'Blessés graves · cumul', value: fmt(s.cumul?.blesses_graves), evoSign: 'up', sub: 'depuis le 1ᵉʳ janvier' },
    { label: 'Accidents · cumul', value: fmt(s.cumul?.accidents), evoSign: 'flat', sub: 'depuis le 1ᵉʳ janvier' },
  ];

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-10 mb-16" data-testid="onisr-mai-2026-page">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Baromètre ONISR — {label}</span>
        </nav>

        {/* Hero */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-7 lg:p-9 mb-7 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/12 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
              <FileBarChart size={13} className="text-red-400" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Données officielles · ONISR · Ministère de l&apos;Intérieur</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[34px] sm:text-[44px] lg:text-[54px] leading-[1.02] tracking-tight">
              Baromètre <span className="text-red-500">{label}</span><br />
              de la sécurité routière
            </h1>
            <p className="text-white/70 text-[15px] mt-5 max-w-2xl leading-relaxed">
              Les chiffres officiels de l&apos;<span className="text-red-400 font-semibold">Observatoire National Interministériel de la Sécurité Routière</span> récupérés automatiquement chaque mois.
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap text-[12px] text-white/55 font-mono-spec">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Mise à jour…' : fetchedAt ? `Dernière mise à jour : ${fetchedAt.toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })}` : 'Données de référence'}
              {stale && <span className="text-amber-400">⚠ source temporairement indisponible · données en cache</span>}
              {error && !data && <span className="text-amber-400">⚠ source indisponible · données de référence</span>}
            </div>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <button
                onClick={() => { setSlideIdx(0); setPresenting(true); }}
                data-testid="open-presentation"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold shadow-lg shadow-red-900/40 transition"
              >
                <Presentation size={14} /> Mode présentation · pour RDV en salle
              </button>
              <button
                onClick={() => window.print()}
                data-testid="print-page"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition"
              >
                <Printer size={14} /> Imprimer la fiche
              </button>
              <button
                onClick={() => loadData(true)}
                disabled={refreshing}
                data-testid="force-refresh"
                title="Force la vérification du dernier baromètre disponible (utile pour vérifier la sortie du prochain mois)"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition disabled:opacity-50"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Vérification…' : 'Forcer la mise à jour'}
              </button>
            </div>
          </div>
        </div>

        <h2 className="font-serif-editorial text-white text-[24px] lg:text-[28px] mb-4">{label.charAt(0).toUpperCase() + label.slice(1)} — France métropolitaine</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {headline.map((s) => <StatCard key={s.label} {...s} />)}
        </div>

        {s.headline?.tues_delta != null && s.headline.tues_delta > 0 && (
          <div className="rounded-2xl border-2 border-red-700/40 bg-red-950/15 p-5 lg:p-6 mb-10 flex items-start gap-4">
            <AlertTriangle size={22} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1.5">Tendance préoccupante</div>
              <p className="text-white text-[15px] leading-relaxed">
                <span className="font-bold">+{s.headline.tues_delta} tués</span> par rapport au même mois l&apos;an dernier. La mortalité augmente principalement chez les usagers vulnérables (cyclistes, piétons).
              </p>
            </div>
          </div>
        )}

        <h2 className="font-serif-editorial text-white text-[24px] lg:text-[28px] mb-4">Cumul depuis janvier {d.data_year || '2026'}</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {cumul.map((s) => <StatCard key={s.label} {...s} />)}
        </div>

        {/* Évolutions par usager */}
        {s.evolutions?.usagers_tues?.length > 0 && (
          <>
            <h2 className="font-serif-editorial text-white text-[24px] lg:text-[28px] mb-4">Tués · évolution par type d&apos;usager</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
              {s.evolutions.usagers_tues.map((u) => {
                const sign = deltaSign(u.delta);
                const I = ICON_USAGER[u.label.toLowerCase()] || Car;
                const color = sign === 'up' ? 'text-red-300 border-red-700/40 bg-red-950/15' :
                              sign === 'down' ? 'text-emerald-300 border-emerald-700/30 bg-emerald-950/15' :
                              'text-white/70 border-white/15 bg-white/5';
                return (
                  <div key={u.label} className={`rounded-2xl border p-5 ${color}`}>
                    <I size={22} className="mb-3" />
                    <div className="font-bold text-white text-[15px] leading-tight mb-1.5 capitalize">{u.label}</div>
                    <div className="text-[18px] font-serif-editorial">{u.delta} tués</div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Âge + Réseau */}
        <div className="grid lg:grid-cols-2 gap-5 mb-10">
          {s.evolutions?.ages?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-red-400" />
                <h3 className="font-serif-editorial text-white text-[20px]">Évolution par âge</h3>
              </div>
              <ul className="divide-y divide-white/5">
                {s.evolutions.ages.map((a) => (
                  <li key={a.label} className="py-3 flex items-center justify-between">
                    <span className="text-white/85 text-[14px]">{a.label}</span>
                    <EvoBadge sign={deltaSign(a.delta)}>{a.delta} tués</EvoBadge>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.evolutions?.reseaux?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={16} className="text-red-400" />
                <h3 className="font-serif-editorial text-white text-[20px]">Évolution par réseau</h3>
              </div>
              <ul className="divide-y divide-white/5">
                {s.evolutions.reseaux.map((r) => (
                  <li key={r.label} className="py-3 flex items-center justify-between">
                    <span className="text-white/85 text-[14px] capitalize">{r.label}</span>
                    <EvoBadge sign={deltaSign(r.delta)}>{r.delta === '0' ? 'stable' : `${r.delta} tués`}</EvoBadge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Pour approfondir — Sécurité Routière */}
        <h2 className="font-serif-editorial text-white text-[24px] lg:text-[28px] mb-4">Pour approfondir — sources officielles</h2>
        <div className="grid md:grid-cols-2 gap-3 mb-10">
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-onisr-pdf"
            className="group block rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-700/40 p-5 transition"
          >
            <div className="flex items-start gap-3">
              <FileBarChart size={20} className="text-red-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">PDF officiel · ONISR</div>
                <h3 className="font-serif-editorial text-white text-[18px] leading-tight mt-1">Baromètre {label}</h3>
                <p className="text-white/55 text-[12.5px] mt-2">Le rapport mensuel complet (5 pages). Méthodologie, tableaux détaillés, séries historiques 2017-2026.</p>
                <span className="inline-flex items-center gap-1 text-[12px] text-red-400 font-bold mt-3 group-hover:text-red-300 transition">
                  Télécharger <ExternalLink size={12} />
                </span>
              </div>
            </div>
          </a>
          <a
            href="https://www.onisr.securite-routiere.gouv.fr/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-onisr-home"
            className="group block rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-700/40 p-5 transition"
          >
            <div className="flex items-start gap-3">
              <BookOpen size={20} className="text-red-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Observatoire · onisr.securite-routiere.gouv.fr</div>
                <h3 className="font-serif-editorial text-white text-[18px] leading-tight mt-1">Tous les rapports & baromètres ONISR</h3>
                <p className="text-white/55 text-[12.5px] mt-2">Bilans annuels, analyses thématiques, données départementales, séries historiques complètes depuis 1970.</p>
                <span className="inline-flex items-center gap-1 text-[12px] text-red-400 font-bold mt-3 group-hover:text-red-300 transition">
                  Visiter le site <ExternalLink size={12} />
                </span>
              </div>
            </div>
          </a>
          <a
            href="https://www.securite-routiere.gouv.fr/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-securite-routiere"
            className="group block rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-700/40 p-5 transition"
          >
            <div className="flex items-start gap-3">
              <ShieldAlert size={20} className="text-red-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Sécurité Routière · securite-routiere.gouv.fr</div>
                <h3 className="font-serif-editorial text-white text-[18px] leading-tight mt-1">Campagnes, conseils, démarches officielles</h3>
                <p className="text-white/55 text-[12.5px] mt-2">Le site institutionnel de la Sécurité Routière : campagnes nationales, conseils pratiques, dossiers thématiques, démarches administratives liées au permis.</p>
                <span className="inline-flex items-center gap-1 text-[12px] text-red-400 font-bold mt-3 group-hover:text-red-300 transition">
                  Visiter le site <ExternalLink size={12} />
                </span>
              </div>
            </div>
          </a>
          <a
            href="https://www.securite-routiere.gouv.fr/dangers-de-la-route"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-dossiers-thematiques"
            className="group block rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-700/40 p-5 transition"
          >
            <div className="flex items-start gap-3">
              <BookOpen size={20} className="text-red-400 shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Dangers de la route · pages officielles</div>
                <h3 className="font-serif-editorial text-white text-[18px] leading-tight mt-1">Alcool · Vitesse · Téléphone · Drogue · Fatigue · Jeunes…</h3>
                <p className="text-white/55 text-[12.5px] mt-2">Chaque grand danger routier analysé en détail par les experts du ministère. Idéal pour préparer un cours en salle ou approfondir un thème.</p>
                <div className="mt-3 flex flex-wrap gap-1.5 text-[10.5px]">
                  {[
                    ['vitesse', 'la-vitesse-et-la-conduite'],
                    ['téléphone', 'le-telephone-et-la-conduite'],
                    ['drogue', 'la-drogue-et-la-conduite'],
                  ].map(([label, slug]) => (
                    <span key={slug} className="px-2 py-0.5 rounded-md bg-red-600/15 border border-red-700/30 text-red-300 font-mono-spec uppercase tracking-wider">{label}</span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1 text-[12px] text-red-400 font-bold mt-3 group-hover:text-red-300 transition">
                  Voir tous les dangers <ExternalLink size={12} />
                </span>
              </div>
            </div>
          </a>
        </div>

        {/* Agenda RDV pédagogique 45 min */}
        <div className="rounded-2xl border-2 border-red-700/40 bg-gradient-to-br from-[#1a0b0c] via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8 mb-6" data-testid="rdv-agenda">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} className="text-red-400" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Animation prête-à-l\u2019emploi · 45 min</div>
          </div>
          <h2 className="font-serif-editorial text-white text-[26px] lg:text-[32px] leading-tight mb-2">Séance RDV pédagogique en salle</h2>
          <p className="text-white/65 text-[14px] mb-6 max-w-3xl">
            Une séance complète chronométrée pour utiliser ce baromètre en salle avec un groupe d&apos;élèves. <span className="text-white font-semibold">Projecteur + Mode présentation</span> au-dessus = toute l&apos;animation se déroule depuis cette page.
          </p>
          <ol className="space-y-3">
            {[
              { t: '0 → 5 min', titre: 'Accueil & contexte', body: 'Présenter le baromètre ONISR. Projeter la slide de couverture en Mode présentation. Demander : « À votre avis, combien de personnes sont mortes sur les routes en France le mois dernier ? » Recueillir 3-4 estimations.' },
              { t: '5 → 10 min', titre: 'Révélation du chiffre choc', body: 'Projeter la slide « 303 tués ». Marquer un silence. Préciser : « +19 par rapport au même mois l\u2019an dernier. » Aucun commentaire moral — laisser parler les élèves d\u2019abord.' },
              { t: '10 → 20 min', titre: 'Activité 1 · Brise-glace (cf. ci-dessus)', body: 'Projeter les évolutions par usager. Pour chaque catégorie, demander : « Pourquoi ce chiffre ? » Recueillir les hypothèses. Les noter au tableau.' },
              { t: '20 → 35 min', titre: 'Activité 2 · Études de cas en groupe', body: '3 sous-groupes, 3 catégories (cyclistes, piétons, automobilistes). 10 min de réflexion + 5 min de restitution. Mission : 5 comportements concrets à adopter dès demain.' },
              { t: '35 → 42 min', titre: 'Activité 3 · Engagement personnel', body: 'Projeter la slide « Cette semaine, je vais… ». Chaque élève rédige son engagement sur un papier. Plier, garder. À reprendre à la prochaine séance.' },
              { t: '42 → 45 min', titre: 'Synthèse + sources', body: 'Projeter la slide « Sources officielles ». Distribuer l\u2019URL codevoiture.fr/onisr-mai-2026 (QR code possible). Inviter les élèves à approfondir avec les fiches Magazine + Tutos vidéo.' },
            ].map((step, i) => (
              <li key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex gap-4 items-start">
                <div className="shrink-0 inline-flex items-center justify-center min-w-[90px] h-11 px-3 rounded-lg bg-red-600 text-white text-[12.5px] font-bold font-mono-spec">{step.t}</div>
                <div className="flex-1">
                  <div className="font-serif-editorial text-white text-[17px] leading-tight">{step.titre}</div>
                  <p className="text-white/65 text-[13px] mt-1.5 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3">
            <Maximize2 size={16} className="text-red-400 shrink-0 mt-1" />
            <div className="text-white/75 text-[13px] leading-relaxed">
              <span className="font-bold text-white">Conseil pratique :</span> appuie sur le bouton <span className="text-red-400 font-bold">Mode présentation</span> en haut de cette page. Tu obtiens 10 diapositives plein écran navigables avec les <span className="font-mono">flèches ←/→</span>. Idéal pour la projection en salle. La touche <span className="font-mono">Échap</span> ferme la présentation.
            </div>
          </div>
        </div>

        {/* Cadrage pédagogique REMC */}
        <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-red-950/15 via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap size={18} className="text-red-400" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Cadrage pédagogique · REMC</div>
          </div>
          <h2 className="font-serif-editorial text-white text-[26px] lg:text-[32px] leading-tight mb-2">Utiliser ces chiffres en formation</h2>
          <p className="text-white/65 text-[14px] mb-5 max-w-3xl">Chaque grand chiffre du baromètre est rattaché à une <span className="text-red-300 font-semibold">compétence REMC</span> du référentiel. Idéal pour ancrer la séance théorique dans la réalité du terrain.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { code: 'C1', title: 'Maniement du véhicule', stat: 'Distance de sécurité, freinage d\u2019urgence', link: '/cours/eleve/bilans' },
              { code: 'C2', title: 'Route & circulation', stat: 'Vulnérables (cyclistes +12, piétons +7)', link: '/cours/eleve/bilans' },
              { code: 'C3', title: 'Conditions difficiles', stat: 'Autoroutes +17 tués · vigilance, fatigue', link: '/cours/eleve/bilans' },
              { code: 'C4', title: 'Conduite autonome', stat: 'Jeunes 18-24 +6 · 25-64 +16 (probatoire & adultes actifs)', link: '/cours/eleve/bilans' },
            ].map((c) => (
              <Link key={c.code} to={c.link} data-testid={`remc-link-${c.code}`} className="group rounded-xl border border-white/10 bg-[#0d0d0e] hover:border-red-700/40 p-4 transition block">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Compétence {c.code}</div>
                <div className="font-serif-editorial text-white text-[18px] leading-tight mt-1">{c.title}</div>
                <p className="text-white/55 text-[12.5px] mt-2 leading-relaxed">{c.stat}</p>
                <span className="inline-flex items-center gap-1 text-[11.5px] text-red-400 font-bold mt-3 group-hover:text-red-300 transition">
                  Évaluer le bilan REMC →
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* 3 activités pédagogiques pour l'enseignant */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 lg:p-8 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Target size={18} className="text-red-400" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Activités clé-en-main · pour l\u2019enseignant ECSR</div>
          </div>
          <h2 className="font-serif-editorial text-white text-[24px] lg:text-[28px] leading-tight mb-5">3 séances utilisant les chiffres ONISR</h2>
          <div className="space-y-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-start gap-3">
                <MessageSquare size={18} className="text-red-400 shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-red-300 font-bold font-mono-spec">Activité 1 · 15 min · brise-glace</div>
                  <h3 className="font-serif-editorial text-white text-[19px] leading-tight mt-1">« Lis le chiffre, propose une cause »</h3>
                  <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">
                    Projetez successivement les chiffres suivants au tableau. Pour chacun, demandez aux élèves de proposer <span className="text-white font-semibold">trois causes possibles</span> avant de révéler la réponse de l&apos;ONISR.
                  </p>
                  <ul className="mt-3 space-y-1.5 text-white/75 text-[13px]">
                    <li>• <span className="text-red-300 font-bold">+12 cyclistes tués</span> en un mois — qu&apos;est-ce qui change en mai dans le trafic ?</li>
                    <li>• <span className="text-red-300 font-bold">+17 tués sur autoroute</span> — vitesse, fatigue, distance, téléphone ?</li>
                    <li>• <span className="text-emerald-300 font-bold">−9 tués chez les motards</span> — équipement, météo, comportement ?</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-start gap-3">
                <ListChecks size={18} className="text-amber-400 shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-300 font-bold font-mono-spec">Activité 2 · 25 min · études de cas</div>
                  <h3 className="font-serif-editorial text-white text-[19px] leading-tight mt-1">« Du chiffre au comportement »</h3>
                  <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">
                    Divisez la classe en 3 groupes. Chacun travaille sur une catégorie en hausse (cyclistes, piétons, occupants VT). Mission : <span className="text-white font-semibold">identifier 5 comportements concrets</span> que l&apos;élève peut adopter dès demain pour réduire ce risque.
                  </p>
                  <p className="text-white/55 text-[12.5px] mt-2 italic">Restitution en plénière. Synthèse animée par l&apos;enseignant. Lien direct avec la <Link to="/cours/eleve/infractions-sanctions" className="text-red-400 hover:text-red-300 underline underline-offset-2">grille Infractions & Sanctions 2026</Link>.</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex items-start gap-3">
                <Lightbulb size={18} className="text-sky-400 shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-sky-300 font-bold font-mono-spec">Activité 3 · 10 min · transfert conduite</div>
                  <h3 className="font-serif-editorial text-white text-[19px] leading-tight mt-1">« Mon engagement de la semaine »</h3>
                  <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">
                    En fin de séance, chaque élève rédige une phrase d&apos;engagement en lien avec un chiffre du baromètre du mois : <span className="text-white italic">« Cette semaine, je vais… parce que… »</span>. À reprendre lors de la prochaine séance de conduite pour ancrer le transfert théorie → pratique.
                  </p>
                  <p className="text-white/55 text-[12.5px] mt-2 italic">Outil idéal pour la compétence C4 « conduite autonome ». Évaluable dans le <Link to="/cours/eleve/bilans" className="text-red-400 hover:text-red-300 underline underline-offset-2">bilan REMC C4</Link>.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* À approfondir dans ton parcours */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 lg:p-8 mb-10">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={18} className="text-red-400" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">À approfondir dans ton parcours codevoiture.fr</div>
          </div>
          <h2 className="font-serif-editorial text-white text-[24px] lg:text-[28px] leading-tight mb-5">Les ressources internes liées</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { to: '/magazine/partage-route-velo-trottinette-en-ville', t: 'Vélo, trottinette : partager la route', s: 'Pourquoi +12 cyclistes tués ?' },
              { to: '/magazine/urgence-sur-autoroute-bande-arret-d-urgence', t: 'Urgence sur autoroute', s: '+17 tués · la séquence qui sauve' },
              { to: '/magazine/gps-telephone-distractions-au-volant', t: 'GPS, téléphone, distractions', s: '5 sec de regard = 125 m en aveugle' },
              { to: '/cours/eleve/infractions-sanctions', t: 'Infractions & Sanctions 2026', s: 'Barème complet 2026 + recherche' },
              { to: '/cours/eleve/bilans', t: 'Bilan REMC C1→C4', s: 'Auto-évaluation interactive' },
              { to: '/tutos', t: 'Vidéos officielles', s: '6 campagnes Sécurité routière' },
            ].map((r) => (
              <Link key={r.to} to={r.to} data-testid={`cross-link-${r.to}`} className="group rounded-xl border border-white/10 bg-white/[0.02] hover:border-red-700/40 hover:bg-white/[0.04] p-4 transition block">
                <div className="font-serif-editorial text-white text-[16px] leading-tight">{r.t}</div>
                <p className="text-white/55 text-[12.5px] mt-1.5">{r.s}</p>
                <span className="inline-flex items-center gap-1 text-[11.5px] text-red-400 font-bold mt-2 group-hover:text-red-300 transition">
                  Lire →
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Ce que ça veut dire */}
        <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-red-950/20 via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8 mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-3">Ce que ça veut dire pour toi, élève</div>
          <h3 className="font-serif-editorial text-white text-[24px] lg:text-[30px] leading-tight mb-4">
            Derrière chaque chiffre, <span className="text-red-500">une vie qui aurait pu être épargnée</span>.
          </h3>
          <ul className="space-y-3 text-white/80 text-[14.5px] leading-relaxed">
            <li className="flex gap-3"><ShieldAlert size={16} className="text-red-400 shrink-0 mt-1" /><span>Les usagers vulnérables (cyclistes, piétons, trottinettes) sont les premiers touchés par toute hausse de la mortalité. Si tu vois un usager vulnérable : ralentis, écarte-toi, jamais d&apos;à-coup.</span></li>
            <li className="flex gap-3"><ShieldAlert size={16} className="text-red-400 shrink-0 mt-1" /><span>Les distances de sécurité, le contrôle de la vigilance et le respect strict des limitations restent les seules parades efficaces.</span></li>
            <li className="flex gap-3"><ShieldAlert size={16} className="text-red-400 shrink-0 mt-1" /><span>Permis probatoire = zone de risque maximal. Chaque trajet doit compter.</span></li>
          </ul>
        </div>

        {/* QR code · pour distribution / handout / partage classe */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 lg:p-8 mb-10" data-testid="onisr-qr-block">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="bg-white p-3 rounded-2xl shadow-2xl shrink-0 self-start" data-testid="onisr-qr-code">
              <QRCodeSVG value={pageUrl} size={160} level="M" includeMargin={false} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <QrCode size={16} className="text-red-400" />
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Partage en classe · QR code de la page</div>
              </div>
              <h3 className="font-serif-editorial text-white text-[22px] lg:text-[26px] leading-tight mb-2">
                Projette ce QR à la fin du RDV pédagogique.
              </h3>
              <p className="text-white/65 text-[13.5px] leading-relaxed">
                Tes élèves scannent avec leur téléphone et retrouvent <span className="text-white font-semibold">la page complète sur leur écran</span> : chiffres ONISR mis à jour automatiquement chaque mois, magazine, vidéos officielles. Idéal pour qu&apos;ils continuent leur réflexion après la séance.
              </p>
              <div className="mt-3 text-red-400 text-[12.5px] font-mono-spec font-bold">
                {pageUrl.replace(/^https?:\/\//, '')}
              </div>
            </div>
          </div>
        </div>

        {/* Méthodologie */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6 mb-10">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/50 font-bold font-mono-spec mb-2">Méthodologie & mise à jour</div>
          <p className="text-white/65 text-[13px] leading-relaxed">
            Cette page est <span className="text-white font-semibold">mise à jour automatiquement</span> en récupérant directement le dernier baromètre publié par l&apos;ONISR. Les informations sur les accidents corporels sont transmises par la Police et la Gendarmerie nationales. Le calcul des blessés graves (définition médicale M.AIS3+) repose sur les constatations des forces de l&apos;ordre corrigées par la modélisation ONISR — Université Gustave Eiffel (Registre du Rhône).
          </p>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-6 border-t border-white/5">
          <Link to="/magazine" data-testid="back-to-magazine" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
            <ArrowLeft size={14} /> Tout le magazine
          </Link>
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="onisr-source-pdf"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold shadow-lg shadow-red-900/40 transition"
          >
            Télécharger le baromètre ONISR (PDF) <ExternalLink size={14} />
          </a>
        </div>

        <div className="mt-6 text-[11.5px] text-white/40 font-mono-spec">
          Source : ONISR — Ministère de l&apos;Intérieur · Mise à jour automatique du PDF officiel · codevoiture.fr décrypte les chiffres officiels.
        </div>
      </section>

      {presenting && (
        <PresentationMode
          slideIdx={slideIdx}
          setSlideIdx={setSlideIdx}
          onClose={() => setPresenting(false)}
          stats={s}
          label={label}
          pageUrl={pageUrl}
        />
      )}
    </PageShell>
  );
}

